import { createBrowserRouter, Outlet } from 'react-router';
import { LoginPage } from './features/auth/login-page';
import { RequireAuth } from './features/auth/require-auth';
import { Shell } from './components/layout/shell';
import { RouteErrorBoundary } from './components/feedback/route-error-boundary';

/**
 * Every page is lazy-loaded so the initial bundle ships only the auth +
 * layout primitives (Vercel rule `bundle-dynamic-imports`). The /login
 * route is eager because it's the first thing an unauthenticated user
 * sees — paying a code-split fetch to render it would be a regression.
 *
 * Route map (validated 2026-05-03 with stakeholder):
 *   /                      → DashboardPage
 *   /sales/new             → SaleNewPage           (POS)
 *   /sales                 → SalesListPage
 *   /products              → ProductsPage
 *   /customers             → CustomersPage
 *   /accounting/journal    → JournalPage
 *   /accounting/ledger     → LedgerPage            (STUB sprint 4)
 *   /accounting/balance    → BalancePage           (STUB sprint 4)
 *   /treasury              → TreasuryPage
 *   /cash-register         → CashRegisterPage      (STUB sprint 2)
 *   /inventory             → InventoryPage         (STUB sprint 3)
 *   /users                 → UsersPage             (STUB sprint 3)
 *   /settings              → SettingsPage
 *   *                      → NotFoundPage
 */
export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <Shell />
      </RequireAuth>
    ),
    // Authenticated tree — RouteErrorBoundary catches anything thrown in
    // any descendant route's render/loader so users never see React
    // Router's default "Hey developer 👋" dev page.
    // See memory `feedback_react-router-error-boundary.md`.
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        lazy: async () => {
          const { DashboardPage } = await import('./features/dashboard/dashboard-page');
          return { Component: DashboardPage };
        },
      },
      {
        path: 'sales/new',
        lazy: async () => {
          const { SaleNewPage } = await import('./features/sales/sale-new-page');
          return { Component: SaleNewPage };
        },
      },
      {
        path: 'sales',
        lazy: async () => {
          const { SalesListPage } = await import('./features/sales/sales-list-page');
          return { Component: SalesListPage };
        },
      },
      {
        path: 'products',
        lazy: async () => {
          const { ProductsPage } = await import('./features/products/products-page');
          return { Component: ProductsPage };
        },
      },
      {
        path: 'products/:productId/variants',
        lazy: async () => {
          const { VariantsPage } = await import('./features/variants/variants-page');
          return { Component: VariantsPage };
        },
      },
      {
        path: 'customers',
        lazy: async () => {
          const { CustomersPage } = await import('./features/customers/customers-page');
          return { Component: CustomersPage };
        },
      },
      {
        path: 'accounting',
        element: <Outlet />,
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: 'journal',
            lazy: async () => {
              const { JournalPage } = await import('./features/accounting/journal-page');
              return { Component: JournalPage };
            },
          },
          {
            path: 'ledger',
            lazy: async () => {
              const { LedgerPage } = await import('./features/accounting/ledger-page');
              return { Component: LedgerPage };
            },
          },
          {
            path: 'balance',
            lazy: async () => {
              const { BalancePage } = await import('./features/accounting/balance-page');
              return { Component: BalancePage };
            },
          },
        ],
      },
      {
        path: 'treasury',
        lazy: async () => {
          const { TreasuryPage } = await import('./features/treasury/treasury-page');
          return { Component: TreasuryPage };
        },
      },
      {
        path: 'cash-register',
        lazy: async () => {
          const { CashRegisterPage } = await import('./features/cash-register/cash-register-page');
          return { Component: CashRegisterPage };
        },
      },
      {
        path: 'inventory',
        lazy: async () => {
          const { InventoryPage } = await import('./features/inventory/inventory-page');
          return { Component: InventoryPage };
        },
      },
      {
        path: 'users',
        lazy: async () => {
          const { UsersPage } = await import('./features/users/users-page');
          return { Component: UsersPage };
        },
      },
      {
        path: 'settings',
        lazy: async () => {
          const { SettingsPage } = await import('./features/settings/settings-page');
          return { Component: SettingsPage };
        },
      },
      {
        path: '*',
        lazy: async () => {
          const { NotFoundPage } = await import('./features/not-found-page');
          return { Component: NotFoundPage };
        },
      },
    ],
  },
]);
